import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { AdminFonctionnelControllerApi } from 'lib_api/lib/api/gen';

import {
  TablePagination,
  TablePaginationWithSort,
  TableSort,
} from 'components/WrappedComponents/Table/types';
import { useApi } from 'hooks/ApiStoreContext';
import { backAlertMessage } from 'hooks/utils/backAlertMessage';
import { PAGINATION_DEFAULT } from 'utils/DashboardUtils';

import { useHandleBackErrors } from '../../../../../hooks/utils/handleBackErrors';
import { ReferentielSearchType } from '../../../Referentiels/Dashboards/utils';
import { FilterDashboardFluxId } from '../../types';

import {
  FetchFluxResponse,
  FetchFluxWarningResponse,
  FluxAlienationDto,
  FluxAlienationWarningDto,
  SearchAlienationResponse,
} from './types';
export function useSearchFluxsAlienation<
  FluxDto extends FluxAlienationDto,
  ApiSortDto extends object,
  SortOptions extends string = string,
>(
  sortDto: TableSort<SortOptions>,
  pagination: TablePagination,
  getControllerFunction: (
    controller: AdminFonctionnelControllerApi,
  ) => (
    param: TablePaginationWithSort<ApiSortDto>,
  ) => Promise<FetchFluxResponse<FluxDto>>,
  buildBackSort: (sortFront: TableSort<SortOptions>) => ApiSortDto,
): SearchAlienationResponse<FluxDto> {
  const controller = useApi().AdminFonctionnelControllerApi;

  return [
    () => {
      return getControllerFunction(controller)({
        sortDto: buildBackSort(sortDto),
        page: pagination.page - 1,
        pageSize: pagination.pageSize,
      });
    },
    result => {
      return {
        data: result.flux,
        total: result.total,
      };
    },
  ];
}

export function useSearchWarningFluxsAlienation<
  WarningDto extends FluxAlienationWarningDto,
>(
  fluxId: string,
  getControllerFunction: (
    controller: AdminFonctionnelControllerApi,
  ) => (
    idFlux: string,
    param: TablePagination,
  ) => Promise<FetchFluxWarningResponse<WarningDto>>,
): ReferentielSearchType<WarningDto, FilterDashboardFluxId> {
  const [pagination, setPagination] =
    useState<TablePagination>(PAGINATION_DEFAULT);
  const [filter, setFilter] = useState<FilterDashboardFluxId>({});

  const controller = useApi().AdminFonctionnelControllerApi;

  return {
    fetchData: async () => {
      const result = await getControllerFunction(controller)(fluxId || '', {
        page: pagination.page - 1,
        pageSize: pagination.pageSize,
      }).catch((e: Response) => {
        void backAlertMessage(e);
        throw e;
      });
      return {
        data: result.warnings,
        total: result.total,
      };
    },
    pagination: pagination,
    setPagination: newPagination => {
      setPagination(newPagination);
    },
    filter: filter,
    setFilter: setFilter,
    refreshDashboard: () => {
      setFilter(filter);
    },
  };
}

export function useFetchSingleFluxAlienation<
  FluxType extends FluxAlienationDto,
>(
  id: string,
  getControllerFunction: (
    controller: AdminFonctionnelControllerApi,
  ) => (idFlux: string) => Promise<FluxType>,
): [FluxType | null, Dispatch<SetStateAction<FluxType | null>>] {
  const behaviourOnError = useHandleBackErrors();
  const [flux, setFlux] = useState<FluxType | null>(null);
  const controllerApi = useApi().AdminFonctionnelControllerApi;

  useEffect(() => {
    getControllerFunction(controllerApi)(id)
      .then(setFlux)
      .catch(behaviourOnError);
  }, [controllerApi, id, behaviourOnError, getControllerFunction]);

  return [flux, setFlux];
}
